import { useCallback } from 'react';
import useDialog from '@/components/organisms/useDialog';

export type RestrictionType = 'captionTranslation' | 'multiLanguageSupport';

export type RestrictionReason = 'plan';

export interface RestrictionConfig {
  heading: string;
  message: string;
  primaryActionTitle?: string;
  secondaryActionTitle?: string;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
}

export interface RestrictionOptions {
  reason?: RestrictionReason;
  customHeading?: string;
  customMessage?: string;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
  customConfig?: Partial<RestrictionConfig>;
}

export const RESTRICTION_CONFIGS: Record<RestrictionType, RestrictionConfig> = {
  captionTranslation: {
    heading: 'Multi Language Support Not Available',
    message:
      'Multi language video processing and subtitles is not included in your current plan. Contact Goldcast to upgrade and access this feature.',
    primaryActionTitle: 'OK',
    secondaryActionTitle: 'Contact Sales'
  },
  multiLanguageSupport: {
    heading: 'Multi Language Support Not Available',
    message:
      'Multi language video processing and subtitles is not included in your current plan. Contact Goldcast to upgrade and access this feature.',
    primaryActionTitle: 'OK',
    secondaryActionTitle: 'Contact Sales'
  }
};

export const useFeatureRestriction = (restrictionType: RestrictionType, options: RestrictionOptions = {}) => {
  const {
    reason = 'plan',
    customHeading,
    customMessage,
    onPrimaryAction,
    onSecondaryAction,
    customConfig = {}
  } = options;

  const {
    isOpen: isRestrictedDialogOpen,
    openDialog: openRestrictedDialog,
    closeDialog: closeRestrictedDialog
  } = useDialog();

  const handleRestrictedAction = useCallback(() => {
    openRestrictedDialog();
  }, [openRestrictedDialog]);

  const handleRestrictedActionClose = useCallback(() => {
    closeRestrictedDialog();
  }, [closeRestrictedDialog]);

  const baseConfig = RESTRICTION_CONFIGS[restrictionType];

  const restrictionConfig: RestrictionConfig = {
    ...baseConfig,
    ...customConfig,
    heading: customHeading || baseConfig.heading,
    message: customMessage || baseConfig.message,
    onPrimaryAction: onPrimaryAction || baseConfig.onPrimaryAction,
    onSecondaryAction: onSecondaryAction || baseConfig.onSecondaryAction
  };

  return {
    isRestrictedDialogOpen,
    openRestrictedDialog,
    closeRestrictedDialog,
    handleRestrictedAction,
    handleRestrictedActionClose,
    restrictionConfig,
    restrictionType,
    restrictionReason: reason
  };
};
